// Interfaces
import IAppointmentBookingRequest from "../interfaces/IAppointmentBookingRequest";
import IAppointmentBookingResponse from "../interfaces/IAppointmentBookingResponse";
import ILocation from "../interfaces/ILocation";
import IAvailableSlot from "../interfaces/IAvailableSlot";
import IAppointmentRecord from "../interfaces/IAppointmentRecord";
import IPatientDetails from "../interfaces/IPatientDetails";
import IStageTwoParams from "../interfaces/IStageTwoParams";
import IStageThreeParams from "../interfaces/IStageThreeParams";
import IMedicareCard from "../interfaces/IMedicareCard";
import IConcessionCard from "../interfaces/IConcessionCard";

// Config
import { LOCAL_HOST, PRACTIQ_API_ENDPOINT, PRACTIQ_API_KEY } from "../utility/config";

// NPM
import Axios, { AxiosResponse } from "axios";
import moment from "moment";

// Classes
import DateTime from "./DateTime";
import Patient from "./Patient";
import Joi from "joi";

const URL =
	process.env.NODE_ENV === "production" ? PRACTIQ_API_ENDPOINT : LOCAL_HOST;

export default class AppointmentBookingRequest {
	public static schema = Joi.object({
		appointmentTypeId: Joi.number().required().min(1),
		doctorId: Joi.number().required().min(1),
		organisationId: Joi.number().required().min(1),
		branchId: Joi.number().required().min(1),
		givenName: Joi.string().required().min(2),
		middleName: Joi.string().optional(),
		familyName: Joi.string().required().min(2),
		mobileNumber: Joi.string().required(),
		email: Joi.string().optional(),
		dateOfBirth: Joi.string().required(),
		appointmentDate: Joi.string().required(),
		appointmentStartTime: Joi.string().required(),
		reason: Joi.string().required().max(255),
		preferredAttendanceMethod: Joi.string()
			.required()
			.valid("ONSITE", "TELEHEALTH"),
		medicareCard: Joi.object({
			medicareNumber: Joi.string().required(),
			irn: Joi.number().required().min(1),
			expiry: Joi.string().required(),
		}).optional(),
		concessionCard: Joi.object({
			cardNumber: Joi.string().required(),
			letter: Joi.string().length(1).required(),
			expireAt: Joi.string().required(),
		}).optional(),
		studentCard: Joi.object({
			institutionName: Joi.string().required(),
			cardNumber: Joi.string().required(),
			expireAt: Joi.string().required(),
		}).optional(),
		sex: Joi.string().required(),
	}).options({ abortEarly: false });

	public static async startBookingStageOne(
		apptReq: IAppointmentBookingRequest
	): Promise<AxiosResponse<IAppointmentBookingResponse>> {
		return await Axios.request<IAppointmentBookingResponse>({
			url: URL + "/appointment/booking-stage-1",
			data: apptReq,
			method: "POST",
			headers: {
				"X-Api-Key": PRACTIQ_API_KEY
			}
		});
	}

	public static async getVerificationCode(stageId: string, channel: number) : Promise<AxiosResponse> {
		return await Axios.request<IAppointmentBookingResponse>({
			url: URL + `/appointment/get-code/${stageId}?channel=${channel}`,
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"X-Api-Key": PRACTIQ_API_KEY
			},
		});
	}

	public static async sendAssistanceRequest(stageId: string): Promise<AxiosResponse> {
		return await Axios.request<IAppointmentBookingResponse>({
			url: URL + `/appointment/need-assistance/${stageId}`,
			method: "POST",
			data: {},
			headers: {
				"Content-Type": "application/json",
				"X-Api-Key": PRACTIQ_API_KEY
			},
		});
	}

	public static async startBookingStageTwo(
		data: IStageTwoParams
	): Promise<AxiosResponse<IAppointmentBookingResponse>> {
		return await Axios.request<IAppointmentBookingResponse>({
			url: URL + "/appointment/booking-stage-2",
			data: { stageId: data.stageId, token: data.verificationCode, receivedVia: data.receivedVia },
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Api-Key": PRACTIQ_API_KEY
			},
		});
	}

	public static async startBookingStageThree(
		data: IStageThreeParams
	): Promise<AxiosResponse<IAppointmentBookingResponse>> {
		return await Axios.request<IAppointmentBookingResponse>({
			url: URL + "/appointment/booking-stage-3",
			data: { ...data },
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Api-Key": PRACTIQ_API_KEY
			},
		});
	}

	public static async resumeBooking(
		stageId: string
	): Promise<AxiosResponse<IAppointmentBookingResponse>> {
		return await Axios.request<IAppointmentBookingResponse>({
			url: URL + "/appointment/resume-booking",
			data: '"' + stageId + '"',
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Api-Key": PRACTIQ_API_KEY
			},
		});
	}

	public static parsePatientDetails(
		patientDetails: IPatientDetails,
		medicareCard: IMedicareCard,
		concessionCard: IConcessionCard,
		apptReq: IAppointmentBookingRequest
	): IAppointmentBookingRequest {
		const reason = patientDetails.telehealthRestrictionAcknowledged
			? patientDetails.notes + " | Telehealth restriction acknowledged."
			: patientDetails.notes;

		const parsedReq: IAppointmentBookingRequest = {
			...apptReq,
			givenName: patientDetails.givenName,
			familyName: patientDetails.familyName,
			mobileNumber: patientDetails.mobile,
			dateOfBirth: Patient.formatDob(patientDetails),
			reason: reason,
			preferredAttendanceMethod: patientDetails.isTelehealth
				? "TELEHEALTH"
				: "ONSITE",
			sex: patientDetails.sex,
		};

		if (patientDetails.middleName) {
			parsedReq.middleName = patientDetails.middleName;
		}

		if (patientDetails.email) {
			parsedReq.email = patientDetails.email;
		}

		if (patientDetails.hasMedicare) {
			parsedReq.medicareCard = {
				medicareNumber: medicareCard.medicareNumber,
				irn: medicareCard.irn,
				expiry: medicareCard.expiry,
			};
		}

		if (patientDetails.hasConcession) {
			parsedReq.concessionCard = {
				cardNumber: concessionCard.cardNumber,
				letter: concessionCard.letter,
				expireAt: moment(concessionCard.expireAt).format("yyyy-MM-DD"),
			};
		}

		if (patientDetails.isStudent) {
			parsedReq.studentCard = {
				institutionName: patientDetails.institutionName,
				cardNumber: patientDetails.studentNumber,
				expireAt: patientDetails.studentNumberExpiry,
			};
		}

		return parsedReq;
	}

	public static parseLocationDetails(
		location: ILocation,
		apptReq: IAppointmentBookingRequest
	): IAppointmentBookingRequest {
		return {
			...apptReq,
			organisationId: location.organisationId,
			branchId: location.branchId,
		};
	}

	public static parseAppointmentType(
		apptTypeId: number,
		apptReq: IAppointmentBookingRequest
	): IAppointmentBookingRequest {
		return {
			...apptReq,
			appointmentTypeId: apptTypeId,
		};
	}

	public static parseAppointmentRecord(
		apptRec: IAppointmentRecord,
		apptReq: IAppointmentBookingRequest
	): IAppointmentBookingRequest {
		return {
			...apptReq,
			doctorId: apptRec.userId,
		};
	}

	public static parseAvailableSlot(
		slot: IAvailableSlot,
		apptReq: IAppointmentBookingRequest
	): IAppointmentBookingRequest {
		return {
			...apptReq,
			appointmentDate: moment(slot.startDateTime).format(
				DateTime.DEFAULT_DATE_FORMAT
			),
			appointmentStartTime: moment(slot.startDateTime).utc().format(),
		};
	}

	public static parseAppointment(
		slot: IAvailableSlot,
		apptRec: IAppointmentRecord,
		apptReq: IAppointmentBookingRequest
	): IAppointmentBookingRequest {
		return AppointmentBookingRequest.parseAppointmentRecord(
			apptRec,
			AppointmentBookingRequest.parseAvailableSlot(slot, apptReq)
		);
	}
}
